import React, { useMemo } from 'react';

import { format, parseISO } from 'date-fns';
import { Box, ButtonV2, Divider, Flex, TextV2 } from '@withjoy/joykit';
import ORDER_PREVIEW_IMG from '../../assets/order_preview.png';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { Link } from '@shared/core';
import OrderInfo from '@apps/card/components/Blocks/OrderInfo';
import { ProgressTracker } from '../ProgressTracker';
import { StationeryPrintOrderDetailsFragment, StationeryPrintOrderStatusEnum } from '@graphql/generated';
import { PaymentSummary } from './components/PaymentSummary';
import { InlineMessage } from './components/InlineMessage';
import { PRINT_ORDER_STATUS_TO_CONSOLIDATED_STATUS } from '../../OrderDetails.constants';

const OrderMetadata = ({ orderNumber, orderPlaced }: { orderNumber: string; orderPlaced?: string }) => {
  const orderPlacedFormatted = orderPlaced && format(parseISO(orderPlaced), 'PPP');
  return (
    <Flex rowGap={2} flexDirection="column" textAlign={{ md: 'right' }} paddingX={{ _: 6, sm2: 8, md: 0 }}>
      <TextV2 typographyVariant="body1">{`Order Number: ${orderNumber}`}</TextV2>
      {orderPlacedFormatted && <TextV2 typographyVariant="body1">{`Order Placed: ${orderPlacedFormatted}`}</TextV2>}
    </Flex>
  );
};

interface PrintJobDetailsProps {
  order: StationeryPrintOrderDetailsFragment;
  variant: 'overview' | 'detailed';
  getToOrderDetailsPath?: (printOrderNumber: string) => string;
}

/**
 * TODO:
 *  - print order preview thumbnail
 */
export const PrintJobDetails = (props: PrintJobDetailsProps) => {
  const { variant, order, getToOrderDetailsPath } = props;
  const isMobileOrTablet = useIsMobileOrTablet();

  const orderNumber = useMemo(() => order.orderNumber, [order.orderNumber]);
  const orderPlaced = useMemo(() => order.printPayment?.paidAt, [order.printPayment?.paidAt]);

  const shippingAddress = useMemo(() => {
    const { address1, address2, city, name, region, postalCode, countryCode } = order.shippingAddress;
    let addressLine = address1;
    if (address2) {
      addressLine += `, ${address2}`;
    }
    const cityLine = `${city}, ${region} ${postalCode} ${countryCode}`;
    return `${name}\n${addressLine}\n${cityLine}`;
  }, [order.shippingAddress]);

  const isErrorStatus = useMemo(() => order.currentStatus === StationeryPrintOrderStatusEnum.error, [order.currentStatus]);
  const consolidatedOrderStatus = PRINT_ORDER_STATUS_TO_CONSOLIDATED_STATUS[order.currentStatus] ?? 'orderPlaced';

  return (
    <Flex flexDirection="column" rowGap={10}>
      <Flex columnGap={{ md: 10 }} flexDirection={{ _: 'column', md: 'row' }} rowGap={10}>
        <Flex flexDirection="column" rowGap={8} flex={1}>
          {isMobileOrTablet && <OrderMetadata orderNumber={orderNumber} orderPlaced={orderPlaced} />}

          <Flex paddingX={{ _: 6, md: 0 }} justifyContent="center">
            <Box
              as="img"
              src={ORDER_PREVIEW_IMG}
              aspectRatio={'5 / 7'}
              __css={{
                width: { _: 360, md: '100%' }
              }}
            />
          </Flex>
        </Flex>

        <Flex flexDirection="column" paddingY={{ md: 8 }} flex={1} maxWidth="629px" rowGap={10}>
          {!isMobileOrTablet && <OrderMetadata orderNumber={orderNumber} orderPlaced={orderPlaced} />}

          <ProgressTracker status={consolidatedOrderStatus} />
          {isErrorStatus && <InlineMessage />}
          <Flex
            columnGap={10}
            flexDirection={{
              _: 'column',
              md: 'row'
            }}
            rowGap={10}
            width={{ _: '100%', sm2: 400, md: 'unset' }}
            paddingX={{ _: 6, sm2: 0 }}
            alignSelf={{ _: 'center', md: 'unset' }}
          >
            <Box flex={1}>
              <OrderInfo iconVariant="pin" title="Shipping To" description={shippingAddress} />
            </Box>

            <Flex flexDirection="column" rowGap={5} flexBasis={{ md: 249 }}>
              <ButtonV2 intent="neutral" shape="rounded">
                Track Your Order
              </ButtonV2>
              {variant === 'overview' && getToOrderDetailsPath && (
                <ButtonV2 as={Link} to={getToOrderDetailsPath(orderNumber)} intent="neutral" variant="outline" shape="rounded">
                  Order Details
                </ButtonV2>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      {variant === 'detailed' && (
        <>
          <Divider />
          <PaymentSummary order={props.order} />
        </>
      )}
    </Flex>
  );
};
